import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AccessGuard, UpgradePrompt } from './access';
import { hasFeatureAccess, getRequiredTierForRoute, TIER_ORDER } from '../data/featureAccess';
import { SubscriptionTier } from '../../shared/types/access';

const API_URL = import.meta.env.VITE_API_URL || '';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requirePlan?: 'monthly' | 'quarterly' | 'yearly';
  requireTier?: 'free' | 'base' | 'premium' | 'pro';
  featureId?: string;
  redirectTo?: string;
}

interface SubscriptionStatus {
  hasSubscription: boolean;
  plan?: string;
  endDate?: string;
  autoRenew?: boolean;
  status?: string;
  tier?: string;
  billing_period?: string;
}

/**
 * SubscriptionGuard — HOC / wrapper component that restricts access
 * to premium features based on the user's subscription status.
 *
 * Usage:
 *   <SubscriptionGuard>
 *     <PremiumFeature />
 *   </SubscriptionGuard>
 *
 *   <SubscriptionGuard redirectTo="/subscription">
 *     <PremiumFeature />
 *   </SubscriptionGuard>
 */
const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  children,
  fallback,
  requirePlan,
  requireTier,
  featureId,
  redirectTo,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
  const [checking, setChecking] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [requiredTierForPrompt, setRequiredTierForPrompt] = useState<SubscriptionTier>('free');

  useEffect(() => {
    if (isLoading) {
      setChecking(true);
      return;
    }

    if (!isAuthenticated || !user) {
      setChecking(false);
      return;
    }

    const checkSubscription = async () => {
      try {
        const res = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const data = await res.json();
        if (data.success) {
          setSubStatus(data.data.subscription);
        }
      } catch {
        // Silently fail — treat as no subscription
      } finally {
        setChecking(false);
      }
    };

    checkSubscription();
  }, [isLoading, isAuthenticated, user]);

  // Show loading while auth or subscription check is in progress
  if (isLoading || checking) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        color: '#d4af37',
        fontSize: '16px',
      }}>
        Проверка подписки...
      </div>
    );
  }

  // Not authenticated — redirect or show fallback
  if (!isAuthenticated || !user) {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return null;
    }
    return (
      fallback || (
        <div style={{
          maxWidth: '500px',
          margin: '60px auto',
          padding: '32px',
          textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(13, 25, 48, 0.8) 0%, rgba(30, 58, 95, 0.6) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h3 style={{ color: '#d4af37', fontSize: '22px', marginBottom: '12px' }}>
            Требуется авторизация
          </h3>
          <p style={{ color: '#aaa', fontSize: '15px', marginBottom: '24px' }}>
            Войдите в аккаунт, чтобы получить доступ к этой функции.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
              border: 'none',
              padding: '12px 32px',
              borderRadius: '10px',
              color: '#0a0a1a',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Войти
          </button>
        </div>
      )
    );
  }

  // Check subscription
  const userTier = subStatus?.tier || 'free';

  // Определяем требуемый тариф
  let requiredTier: string | null = null;
  let featureName: string | undefined;

  if (requireTier) {
    requiredTier = requireTier;
  } else if (featureId) {
    const hasAccess = hasFeatureAccess(userTier, featureId);
    if (!hasAccess) {
      requiredTier = requirePlan ? 'premium' : 'base';
    }
    featureName = featureId;
  } else {
    // Автоматически определяем по route
    requiredTier = getRequiredTierForRoute(location.pathname);
  }

  // Проверяем доступ
  const hasAccess = !requiredTier || TIER_ORDER[userTier as keyof typeof TIER_ORDER] >= TIER_ORDER[requiredTier as keyof typeof TIER_ORDER];

  if (!hasAccess && requiredTier) {
    // Показываем AccessGuard с UpgradePrompt
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <AccessGuard
        featureId={featureId || 'unknown'}
        requiredTier={requiredTier as SubscriptionTier}
      >
        {children}
      </AccessGuard>
    );
  }

  return <>{children}</>;
};

export default SubscriptionGuard;
